import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { plPL } from '@mui/x-data-grid/locales';
import timeTrackerController, { ITimeTrackingIssue } from '../../../Api/TimeTracker';

export type IssuesViewProps = {
  onIssuesCount?: (count: number) => void;
};

type IssueRow = ITimeTrackingIssue & {
  id: string;
  sessionStartedAtDate: Date | null;
};

const activityChipColor = (activity?: string) => {
  const key = (activity || '').toLowerCase();
  if (key === 'wejście' || key === 'wejscie') return 'success' as const;
  if (key === 'przerwa') return 'warning' as const;
  if (key === 'papieros') return 'error' as const;
  if (key === 'piec') return 'info' as const;
  return 'default' as const;
};

const IssuesView: FC<IssuesViewProps> = ({ onIssuesCount }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [status, setStatus] = useState<'open' | 'resolved'>('open');
  const [issues, setIssues] = useState<ITimeTrackingIssue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [resolveBusyId, setResolveBusyId] = useState<string>('');
  const [endAtByIssueId, setEndAtByIssueId] = useState<Record<string, string>>({});

  const [density, setDensity] = useState<'compact' | 'standard' | 'comfortable'>('compact');

  const rowHeightByDensity = useCallback(() => {
    // W tym widoku wiersz musi pomieścić kontrolki (datetime + przyciski),
    // więc traktujemy "density" jako wybór wysokości wiersza.
    if (density === 'compact') return 72;
    if (density === 'comfortable') return 104;
    return 88; // standard
  }, [density]);

  const fetchIssues = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const res = await timeTrackerController.getIssues(status);
    if (!res.ok) {
      setIssues([]);
      setError(res.message || 'Nie udało się pobrać zgłoszeń');
      setIsLoading(false);
      onIssuesCount?.(0);
      return;
    }

    setIssues(res.issues || []);
    setIsLoading(false);

    if (status === 'open') onIssuesCount?.((res.issues || []).length);
  }, [onIssuesCount, status]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const resolveUnknown = useCallback(
    async (issueId: string) => {
      setResolveBusyId(issueId);
      const res = await timeTrackerController.resolveIssue(issueId, { resolution: 'unknown' });
      setResolveBusyId('');
      if (!res.ok) {
        setError(res.message || 'Nie udało się zamknąć zgłoszenia');
        return;
      }
      fetchIssues();
    },
    [fetchIssues]
  );

  const resolveManualEnd = useCallback(
    async (issueId: string) => {
      const endAtLocal = endAtByIssueId[issueId];
      if (!endAtLocal) {
        setError('Wybierz datę i godzinę zakończenia');
        return;
      }

      const endAtIso = new Date(endAtLocal).toISOString();

      setResolveBusyId(issueId);
      const res = await timeTrackerController.resolveIssue(issueId, { resolution: 'manual_end', endAt: endAtIso });
      setResolveBusyId('');
      if (!res.ok) {
        setError(res.message || 'Nie udało się zamknąć zgłoszenia');
        return;
      }
      fetchIssues();
    },
    [endAtByIssueId, fetchIssues]
  );

  const rows = useMemo<IssueRow[]>(() => {
    return issues.map((i) => ({
      id: i.issueId,
      ...i,
      sessionStartedAtDate: i.sessionStartedAt ? new Date(i.sessionStartedAt) : null,
    }));
  }, [issues]);

  const columns: GridColDef<IssueRow>[] = useMemo(
    () => [
      { field: 'sessionStartedAtDate', headerName: 'Start', type: 'dateTime', width: 200, hideable: true },
      {
        field: 'employeeName',
        headerName: 'Pracownik',
        flex: 1,
        minWidth: 220,
        valueGetter: (_value, row) => row.employeeName || 'Nieprzypisana karta',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%', minWidth: 0 }}>
            <Typography noWrap>{String(params.value || '')}</Typography>
          </Box>
        ),
      },
      {
        field: 'activityLabel',
        headerName: 'Aktywność',
        width: 160,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <Chip
              size="small"
              variant="outlined"
              color={activityChipColor(String(params.value || ''))}
              label={params.value || '-'}
            />
          </Box>
        ),
      },
      {
        field: 'sessionStartedAt',
        headerName: 'Godzina startu',
        width: 150,
        valueGetter: (_value, row) => (row.sessionStartedAt ? dayjs(row.sessionStartedAt).format('HH:mm') : '-'),
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <Typography noWrap>{String(params.value || '')}</Typography>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: 'Rozwiązanie',
        width: 420,
        sortable: false,
        filterable: false,
        cellClassName: 'issues-actions-cell',
        renderCell: (params) => {
          if (status !== 'open') return <Typography variant="caption" color="text.secondary">-</Typography>;

          const issueId = params.row.issueId;
          const busy = resolveBusyId === issueId;
          const endAtLocal = endAtByIssueId[issueId] || '';

          return (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                gap={1}
                sx={{
                  width: '100%',
                  flexWrap: { md: 'wrap' },
                  alignItems: { xs: 'stretch', md: 'center' },
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  disabled={busy}
                  onClick={() => resolveUnknown(issueId)}
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  Nie wiem
                </Button>

                <TextField
                  size="small"
                  type="datetime-local"
                  label="Zakończ o"
                  value={endAtLocal}
                  onChange={(e) => setEndAtByIssueId((prev) => ({ ...prev, [issueId]: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 210, flex: 1 }}
                />

                <Button
                  size="small"
                  variant="contained"
                  disabled={busy}
                  onClick={() => resolveManualEnd(issueId)}
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  {busy ? <CircularProgress size={16} color="inherit" /> : 'Zakończ'}
                </Button>
              </Stack>
            </Box>
          );
        },
      },
    ],
    [endAtByIssueId, resolveBusyId, resolveManualEnd, resolveUnknown, status]
  );

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={800}>
              Problemy (brak zakończenia)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Wynik: {rows.length}
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              sx={{ minWidth: 180 }}
              fullWidth={!isDesktop}
            >
              <MenuItem value="open">Otwarte</MenuItem>
              <MenuItem value="resolved">Zamknięte</MenuItem>
            </TextField>

            <Button
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              onClick={fetchIssues}
              disabled={isLoading}
              fullWidth={!isDesktop}
            >
              Odśwież
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            density={density}
            onDensityChange={(next) => setDensity(next)}
            rowHeight={rowHeightByDensity()}
            columnHeaderHeight={52}
            disableRowSelectionOnClick
            loading={isLoading}
            localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
            initialState={{
              sorting: { sortModel: [{ field: 'sessionStartedAtDate', sort: 'desc' }] },
              columns: { columnVisibilityModel: { sessionStartedAtDate: false } },
            }}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 250 },
              },
            }}
            sx={{
              borderRadius: 2,
              borderColor: 'divider',
              '& .issues-actions-cell': {
                alignItems: 'stretch',
                py: 0.5,
                overflow: 'visible',
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default IssuesView;
